import axios from 'axios';
import { ConversationFlow, FlowInfo, FlowValidationResult, Lead, TestMessage, TestResult } from '../types';

// Configuração do axios
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 segundos
});

// Interceptor para logs
api.interceptors.request.use(
  (config) => {
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    console.log(`[API] ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('[API] Response error:', error.response?.status, error.response?.data);
    return Promise.reject(error);
  }
);

export class LeadsAPI {
  static async listLeads(): Promise<Lead[]> {
    try {
      const response = await api.get('/leads');
      return response.data;
    } catch (error) {
      console.error('Erro ao listar leads:', error);
      throw new Error('Erro ao carregar lista de leads');
    }
  }

  static async pauseLead(phone: string): Promise<void> {
    try {
      await api.post(`/leads/${phone}/pause`);
    } catch (error) {
       console.error(`Erro ao pausar lead ${phone}:`, error);
       throw new Error('Erro ao pausar lead');
    }
  }

  static async resumeLead(phone: string): Promise<void> {
    try {
      await api.post(`/leads/${phone}/resume`);
    } catch (error) {
       console.error(`Erro ao resumir lead ${phone}:`, error);
       throw new Error('Erro ao resumir lead');
    }
  }

  static async getBotGlobalStatus(): Promise<'active' | 'paused'> {
    try {
      const response = await api.get('/leads/global/status');
      return response.data.status;
    } catch (error) {
      console.error('Erro ao obter status global do bot:', error);
      return 'active';
    }
  }

  static async pauseBotGlobal(): Promise<void> {
    try {
      await api.post('/leads/global/pause');
    } catch (error) {
      console.error('Erro ao pausar bot globalmente:', error);
      throw new Error('Erro ao pausar bot');
    }
  }

  static async resumeBotGlobal(): Promise<void> {
    try {
      await api.post('/leads/global/resume');
    } catch (error) {
      console.error('Erro ao retomar bot globalmente:', error);
      throw new Error('Erro ao retomar bot');
    }
  }

  static async getCatalog(): Promise<any> {
    try {
      const response = await api.get('/catalog');
      return response.data;
    } catch (error) {
       console.error(`Erro ao obter catálogo:`, error);
       throw new Error('Erro ao obter catálogo de administração');
    }
  }

  static async saveCatalog(blocks: any): Promise<void> {
    try {
      await api.put('/catalog', blocks);
    } catch (error) {
       console.error(`Erro ao salvar catálogo:`, error);
       throw new Error('Erro ao salvar catálogo de administração');
    }
  }
}

export class FlowAPI {
  /**
   * Lista todos os fluxos disponíveis
   */
  static async listFlows(): Promise<FlowInfo[]> {
    try {
      const response = await api.get('/flows');
      return response.data;
    } catch (error) {
      console.error('Erro ao listar fluxos:', error);
      throw new Error('Erro ao carregar lista de fluxos');
    }
  }

  /**
   * Busca um fluxo específico por ID
   */
  static async getFlow(flowId: string): Promise<ConversationFlow> {
    try {
      const response = await api.get(`/flow/${flowId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Fluxo não encontrado');
      }
      console.error('Erro ao buscar fluxo:', error);
      throw new Error('Erro ao carregar fluxo');
    }
  }

  /**
   * Cria um novo fluxo
   */
  static async createFlow(name: string, description?: string): Promise<string> {
    try {
      const response = await api.post('/flow', { name, description: description || '' });
      return response.data.id;
    } catch (error: any) {
      if (error.response?.status === 409) {
        throw new Error('Já existe um fluxo com este nome');
      }
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
        ? detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ')
        : 'Erro ao criar fluxo';
      console.error('Erro ao criar fluxo:', error);
      throw new Error(msg);
    }
  }

  /**
   * Atualiza um fluxo existente
   */
  static async updateFlow(flowId: string, flow: ConversationFlow): Promise<void> {
    try {
      await api.put(`/flow/${flowId}`, flow);
    } catch (error) {
      console.error('Erro ao atualizar fluxo:', error);
      throw new Error('Erro ao salvar fluxo');
    }
  }

  /**
   * Remove um fluxo
   */
  static async deleteFlow(flowId: string): Promise<void> {
    try {
      await api.delete(`/flow/${flowId}`);
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Fluxo não encontrado');
      }
      console.error('Erro ao deletar fluxo:', error);
      throw new Error('Erro ao deletar fluxo');
    }
  }

  /**
   * Valida um fluxo
   */
  static async validateFlow(flowId: string): Promise<FlowValidationResult> {
    try {
      const response = await api.post(`/flow/${flowId}/validate`);
      return response.data;
    } catch (error) {
      console.error('Erro ao validar fluxo:', error);
      throw new Error('Erro ao validar fluxo');
    }
  }

  /**
   * Testa uma mensagem no fluxo
   */
  static async testFlow(flowId: string, testData: TestMessage): Promise<TestResult> {
    try {
      const response = await api.post(`/flow/${flowId}/test`, testData);
      return response.data;
    } catch (error) {
      console.error('Erro ao testar fluxo:', error);
      throw new Error('Erro ao testar mensagem no fluxo');
    }
  }

  /**
   * Cria um fluxo template básico
   */
  static async createTemplate(): Promise<ConversationFlow> {
    try {
      const response = await api.post('/flow/template');
      return response.data;
    } catch (error) {
      console.error('Erro ao criar template:', error);
      throw new Error('Erro ao criar template');
    }
  }

  /**
   * Importa um fluxo a partir de arquivo TypeBot
   */
  static async importFromTypebot(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/import/typebot', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.id;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Arquivo inválido. Certifique-se de que é um JSON do TypeBot.');
      }
      console.error('Erro ao importar TypeBot:', error);
      throw new Error('Erro ao importar arquivo TypeBot');
    }
  }

  /**
   * Exporta um fluxo para formato TypeBot
   */
  static async exportToTypebot(flowId: string): Promise<Blob> {
    try {
      const response = await api.get(`/export/typebot/${flowId}`, {
        responseType: 'blob',
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao exportar para TypeBot:', error);
      throw new Error('Erro ao exportar para TypeBot');
    }
  }

  /**
   * Faz download de um arquivo blob
   */
  static downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Busca palavras-chave que ativam o fluxo
   */
  static async getTriggerWords(flowId: string): Promise<string[]> {
    try {
      const response = await api.get(`/flows/${flowId}/trigger-words`);
      return response.data.trigger_words || [];
    } catch (error) {
      console.error(`Erro ao buscar trigger words para ${flowId}:`, error);
      return [];
    }
  }

  /**
   * Salva palavras-chave que ativam o fluxo
   */
  static async updateTriggerWords(flowId: string, words: string[]): Promise<void> {
    try {
      await api.put(`/flows/${flowId}/trigger-words`, { trigger_words: words });
    } catch (error: any) {
      console.error(`Erro ao salvar trigger words para ${flowId}:`, error);
      throw new Error(error.response?.data?.detail || 'Erro ao salvar palavras-chave');
    }
  }

  /**
   * Ativa um fluxo
   */
  static async switchActiveFlow(flowId: string): Promise<void> {
    try {
      await api.post(`/switch-flow/${flowId}`);
    } catch (error: any) {
      console.error(`Erro ao ativar o fluxo ${flowId}:`, error);
      throw new Error(error.response?.data?.detail || 'Erro ao ativar o fluxo');
    }
  }

  /**
   * Desativa o fluxo ativo
   */
  static async deactivateFlow(): Promise<void> {
    try {
      await api.post('/deactivate-flow');
    } catch (error: any) {
      console.error('Erro ao desativar fluxo:', error);
      throw new Error(error.response?.data?.detail || 'Erro ao desativar o fluxo');
    }
  }

  /**
   * Verifica se a API está online
   */
  static async healthCheck(): Promise<boolean> {
    try {
      const response = await api.get('/');
      return response.status === 200;
    } catch (error) {
      console.error('API não está respondendo:', error);
      return false;
    }
  }
}

// ── CRM API ─────────────────────────────────────────────────────────────────

export interface CrmCard {
  whatsapp: string;
  fase: string;
  notes: string;
  whatsapp_name: string;
  nome: string;
  data_cadastro: string | null;
  updated_at: string | null;
  color: 'red' | 'yellow' | 'green' | null;
}

export interface CrmBoard {
  columns: Record<string, CrmCard[]>;
  total: number;
}

export interface Venda {
  id: number;
  produto: string;
  valor_bruto: number;
  status: string;
  cliente?: string;
  data_hora?: string;
  created_at: string;
  origem?: string;
}

export class CrmAPI {
  static async getBoard(): Promise<CrmBoard> {
    const res = await api.get('/crm/board');
    return res.data;
  }

  static async updateNotes(phone: string, notes: string): Promise<void> {
    await api.patch(`/crm/card/${encodeURIComponent(phone)}/notes`, { notes });
  }

  static async updateName(phone: string, name: string): Promise<void> {
    await api.patch(`/crm/card/${encodeURIComponent(phone)}/name`, { name });
  }

  static async updateFase(phone: string, fase: string): Promise<void> {
    await api.patch(`/crm/card/${encodeURIComponent(phone)}/fase`, { fase });
  }

  static async getVendas(limit: number = 50): Promise<Venda[]> {
    const res = await api.get(`/sales/vendas?limit=${limit}`);
    return res.data.vendas;
  }

  static async deleteVenda(vendaId: number): Promise<void> {
    await api.delete(`/sales/venda/${vendaId}`);
  }
}